import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Tag, Space, Button, message, Modal, Popconfirm, Typography, Tooltip } from 'antd';
import { UserOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { staffApi } from '../../services/manager/staff';
import type { StaffItem } from '../../types/entity/staff';
import { useRoleMap } from '../../services/store/organization.store';
import { UpsertModal, type FormFieldConfig } from '../../components/Templates/UpsertModal';
import List, { ListRef } from '../../components/Templates/List';
import type { FieldConfig } from '../../components/ForgeQueryBuilder';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { label: '启用', value: 1 },
  { label: '禁用', value: 0 },
];

const Staffs: React.FC = () => {
  const listRef = useRef<ListRef>(null);
  const [modalState, setModalState] = useState<{ open: boolean; mode: 'create' | 'edit'; record: StaffItem | null }>({
    open: false,
    mode: 'create',
    record: null
  });

  const { roleList, roleReload } = useRoleMap();

  useEffect(() => {
    roleReload();
  }, []);

  const roleNameMap = useMemo(() => {
    const map = new Map<string, string>();
    roleList.forEach((role) => {
      map.set(role.code.toLowerCase(), role.name);
    });
    return map;
  }, [roleList]);

  const queryFields: FieldConfig[] = [
    { key: 'keyword', label: '关键词', type: 'string', placeholder: '姓名/账号' },
    { key: 'status', label: '状态', type: 'select', options: STATUS_OPTIONS },
  ];

  const formFields: FormFieldConfig[] = [
    { name: 'name', label: '员工姓名', type: 'text', required: true, rules: [{ required: true, message: '请输入员工姓名' }, { max: 100 }] },
    { name: 'username', label: '登录账号', type: 'text', required: true, rules: [{ required: true, message: '请输入登录账号' }, { max: 50 }], disabled: (mode) => mode === 'edit' },
    { 
      name: 'password', 
      label: '登录密码', 
      type: 'password', 
      rules: (mode) => {
        const rules: any[] = [{ pattern: /^(?=.*[A-Za-z])(?=.*\d).{8,}$/, message: '密码需8位以上且包含数字和字符' }, { max: 64 }];
        if (mode === 'create') {
          rules.push({ required: true, message: '请输入登录密码' });
        }
        return rules;
      },
      fieldProps: { placeholder: '如需重置请输入新密码' }
    },
    { 
      name: 'role_ids', 
      label: '员工角色', 
      type: 'select', 
      required: true, 
      options: roleList.map((role) => ({ label: role.name, value: role.id, disabled: role.status !== 1 })),
      fieldProps: { mode: 'multiple' }
    },
    { name: 'status', label: '启用状态', type: 'select', required: true, options: STATUS_OPTIONS, initialValue: 1 },
  ];

  const fetcher = async (params: any) => {
    // params 已经由 buildParams 处理过，直接使用
    const filter: any = {};

    if (params.keyword) {
      filter.keyword = params.keyword;
    }

    // 注意：status 可能是 0（禁用），所以要用 !== undefined 而不是 truthy 判断
    if (params.status !== undefined && params.status !== null) {
      filter.status = Number(params.status);
    }

    console.log('Fetcher params:', params);
    console.log('Filter being sent:', filter);

    const res = await staffApi.list({
      page: params.page || 1,
      pageSize: params.pageSize || 20,
      filter: Object.keys(filter).length > 0 ? filter : undefined
    });

    return {
      rows: res.data.items || [],
      pagination: res.data.pagination
    };
  };

  const renderRoles = (roleCodes: string[]) => {
    if (!roleCodes || roleCodes.length === 0) {
      return <Tag color="default">未分配</Tag>;
    }
    return (
      <Space size={[4, 4]} wrap>
        {roleCodes.map((role) => (
          <Tag key={role} color={role.toLowerCase().includes('admin') ? 'purple' : 'blue'}>
            {roleNameMap.get(role.toLowerCase()) || role}
          </Tag>
        ))}
      </Space>
    );
  };

  const formatTime = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN');
  };

  const handleDelete = async (record: StaffItem) => {
    try {
      const res = await staffApi.deleteById(String(record.id));
      if (res.code !== 200) {
        throw new Error(res.message || '删除失败');
      }
      message.success('员工已删除');
      listRef.current?.reload();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || '删除失败';
      message.error(msg);
    }
  };

  const columns: ColumnsType<StaffItem> = [
    {
      title: '登录账号',
      dataIndex: 'username',
      width: 160,
      render: (username: string) => (
        <Space>
          <UserOutlined style={{ color: '#1677ff' }} />
          <Text>{username}</Text>
        </Space>
      ),
    },
    { title: '员工姓名', dataIndex: 'name', width: 160, render: (t) => t || '-' },
    { title: '角色', dataIndex: 'roles', render: renderRoles },
    {
      title: '状态',
      dataIndex: 'status',
      width: 120,
      render: (status: number) => <Tag color={status === 1 ? 'success' : 'default'}>{status === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      width: 200,
      render: (time?: string) => (
        <Tooltip title={time ? formatTime(time) : '暂无登录记录'}>
          <span>{formatTime(time)}</span>
        </Tooltip>
      ),
    },
    { title: '创建时间', dataIndex: 'created_at', width: 200, render: (t) => formatTime(t) },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => setModalState({ open: true, mode: 'edit', record })}>
            编辑
          </Button>
          <Popconfirm title="确定删除该员工？" onConfirm={() => handleDelete(record)}>
            <Button type="link" icon={<DeleteOutlined />} danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <List<StaffItem>
        ref={listRef}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>员工管理</span>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => listRef.current?.reload()}>刷新</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ open: true, mode: 'create', record: null })}>
                新增员工
              </Button>
            </Space>
          </div>
        }
        queryFields={queryFields}
        fetcher={fetcher}
        buildParams={(q) => {
          // 将 ForgeQuery 格式转换为简单的键值对
          const params: any = {
            page: q.page || 1,
            pageSize: q.pageSize || 20
          };

          // 提取 filter 中的值
          if (q.filter) {
            const filter = q.filter;
            Object.keys(filter).forEach(key => {
              const filterItem = filter[key];
              if (filterItem && typeof filterItem === 'object' && 'value' in filterItem) {
                params[key] = filterItem.value;
              }
            });
          }

          return params;
        }}
        columns={columns}
        rowKey={(r) => String(r.id)}
        tableProps={{ scroll: { x: 'max-content' } }}
      />

      <UpsertModal<StaffItem>
        title={modalState.mode === 'create' ? '新增员工' : '编辑员工'}
        open={modalState.open}
        mode={modalState.mode}
        record={modalState.record}
        onCancel={() => setModalState({ ...modalState, open: false })}
        onSuccess={() => {
          setModalState({ ...modalState, open: false });
          listRef.current?.reload();
        }}
        createApi={(data) => staffApi.create(data)}
        updateApi={(id, data) => staffApi.updateById(id, data)}
        formFields={formFields}
        beforeEdit={(record) => {
          // 将后端数据转换为表单需要的格式
          return {
            ...record,
            role_ids: record.role_ids || []
          };
        }}
        beforeSubmit={(values) => {
           // Cleanup values - 确保所有字段都被传递
           const submitData: any = {
             ...values,
             role_ids: values.role_ids ?? [],
           };

           // 确保 status 字段被明确传递（即使是 0）
           if (values.status !== undefined && values.status !== null) {
             submitData.status = Number(values.status);
           }

           return submitData;
        }}
      />
    </>
  );
};

export default Staffs;
